import { Bell, Search, Sun, Moon, Menu, Plus, X, Pencil, User, CheckSquare, AtSign, Clock, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { useTheme } from '../../context/ThemeContext';

// Supabase client initialization
const supabaseClient = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Arama kategorileri ve örnek veriler
const SEARCH_CATEGORIES = {
  tasks: {
    name: 'Görevler',
    path: '/tasks',
    getResults: (query, items) =>
      items.filter(
        (item) =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
      ),
  },
  team: {
    name: 'Ekip',
    path: '/team',
    getResults: (query, items) =>
      items.filter(
        (item) =>
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.role.toLowerCase().includes(query.toLowerCase())
      ),
  },
  files: {
    name: 'Dosyalar',
    path: '/files',
    getResults: (query, items) =>
      items.filter((item) => item.name.toLowerCase().includes(query.toLowerCase())),
  },
};

const SAMPLE_SEARCH_DATA = {
  tasks: [
    { id: 1, title: 'Yeni özellik geliştirmesi', description: 'UI filtreleme özellikleri' },
    { id: 2, title: 'Hata düzeltmesi', description: 'Performans optimizasyonu' },
    { id: 3, title: 'Dokümantasyon', description: 'API dokümantasyonu güncelleme' },
  ],
  team: [
    { id: 1, name: 'Ahmet Yılmaz', role: 'Takım Lideri' },
    { id: 2, name: 'Mehmet Demir', role: 'Geliştirici' },
    { id: 3, name: 'Ayşe Kara', role: 'Tasarımcı' },
  ],
  files: [
    { id: 1, name: 'Proje Planı.pdf' },
    { id: 2, name: 'Toplantı Notları.docx' },
    { id: 3, name: 'Sunum Görselleri.zip' },
  ],
};

// Bildirim tiplerini tanımla
const NOTIFICATION_TYPES = {
  task: { icon: CheckSquare, color: 'blue' },
  project: { icon: Pencil, color: 'purple' },
  team: { icon: User, color: 'green' },
  mention: { icon: AtSign, color: 'yellow' },
  system: { icon: Bell, color: 'orange' },
  security: { icon: User, color: 'red' },
  deadline: { icon: Clock, color: 'yellow' },
};

// Bildirim zaman formatı için yardımcı fonksiyon
const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return date.toLocaleDateString('tr-TR');
  } else if (days > 0) {
    return `${days} gün önce`;
  } else if (hours > 0) {
    return `${hours} saat önce`;
  } else if (minutes > 0) {
    return `${minutes} dakika önce`;
  } else {
    return 'Az önce';
  }
};

const Navbar = ({ onToggleSidebar, isStockPanel = false }) => {
  const { isDark, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(null);

  const notificationRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Kullanıcı profili çek
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabaseClient.auth.getSession();
        if (sessionError) throw sessionError;
        if (!session) return;

        const { data: profile, error: profileError } = await supabaseClient
          .from('user_profiles')
          .select('first_name, last_name, email, profile_photo_url, job_position')
          .eq('email', session.user.email)
          .single();

        if (profileError) throw profileError;
        if (!profile) throw new Error('Profil bulunamadı');
        setUserProfile(profile);
      } catch (error) {
        console.error('Profile fetch error:', error);
        toast.error('Profil bilgileri alınamadı');
      }
    };
    fetchUserProfile();
  }, []);

  // Dışa tıklayınca kapansın
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Bildirim menüsü
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      // Arama sonuçları
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bildirimler açıkken body scroll engelle
  useEffect(() => {
    if (showNotifications) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [showNotifications]);

  // Arama fonksiyonları
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowResults(query.length > 0);
  };

  const getSearchResults = () => {
    if (!searchQuery) return [];
    return Object.entries(SEARCH_CATEGORIES).map(([key, category]) => {
      const results = category.getResults(searchQuery, SAMPLE_SEARCH_DATA[key]);
      return { category: category.name, path: category.path, results };
    });
  };

  const handleResultClick = (path) => {
    navigate(path);
    setSearchQuery('');
    setShowResults(false);
  };

  // Varsayılan profil fotoğrafı
  const getDefaultProfilePhotoUrl = (firstName = '', lastName = '') => {
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=3b82f6&color=fff`;
  };

  const searchResultsArr = getSearchResults();
  const hasResults = searchResultsArr.some((cat) => cat.results.length > 0);

  // Local storage helpers for system notification states (per-user)
  const getSystemStateKey = (userId, kind) => `system_notifications_${kind}_${userId}`;
  const loadSystemState = (userId, kind) => {
    try {
      const raw = localStorage.getItem(getSystemStateKey(userId, kind));
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (_) {
      return new Set();
    }
  };
  const saveSystemState = (userId, kind, set) => {
    try {
      localStorage.setItem(getSystemStateKey(userId, kind), JSON.stringify(Array.from(set)));
    } catch (_) {}
  };

  // Bildirimleri çek
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      
      // Aktif kullanıcı oturumunu al
      const { data: { session }, error: sessionError } = await supabaseClient.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session) return;
      
      // Kullanıcının kişisel bildirimlerini getir
      const { data: personalNotifications, error: personalNotificationsError } = await supabaseClient
        .from('notifications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (personalNotificationsError) throw personalNotificationsError;

      // Aktif sistem bildirimlerini getir
      const { data: systemNotifications, error: systemNotificationsError } = await supabaseClient
        .from('system_notifications')
        .select('*')
        .eq('is_active', true) // Sadece aktif olanları getir
        .order('created_at', { ascending: false });

      if (systemNotificationsError) throw systemNotificationsError;

      // Kullanıcıya ait sistem okundu/silinmiş durumlarını yükle
      const readSet = loadSystemState(session.user.id, 'read');
      const deletedSet = loadSystemState(session.user.id, 'deleted');

      // Tüm bildirimleri birleştir ve tarihe göre sırala
      const allNotifications = [
        ...(personalNotifications || []),
        ...(systemNotifications || [])
          .filter(sysNotif => !deletedSet.has(sysNotif.id))
          .map(sysNotif => ({
            ...sysNotif,
            is_system: true,
            type: 'system',
            is_read: readSet.has(sysNotif.id),
            system_id: sysNotif.id,
          }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Bildirim varsa, ilgili kullanıcı profillerini çek
      if (allNotifications && allNotifications.length > 0) {
        // Benzersiz oluşturucu ID'lerini topla (null değerler olmadan ve sistem bildirimleri için geçerli değil)
        const creatorIds = [...new Set(
          allNotifications
            .map(notification => notification.created_by)
            .filter(id => id !== null)
        )];

        let creators = [];
        if (creatorIds.length > 0) {
          const { data: fetchedCreators, error: creatorsError } = await supabaseClient
            .from('user_profiles')
            .select('id, first_name, last_name, profile_photo_url')
            .in('id', creatorIds);

          if (!creatorsError && fetchedCreators) {
            creators = fetchedCreators;
          }
        }
            
        // Her bildirime ilgili kullanıcı profilini ekle
        const notificationsWithCreators = allNotifications.map(notification => {
          if (notification.is_system) {
            return notification; // Sistem bildirimleri için oluşturucuya gerek yok
          }
          const creator = creators.find(c => c.id === notification.created_by);
          return {
            ...notification,
            creator
          };
        });
        
        setNotifications(notificationsWithCreators);
        
        // Okunmamış bildirim sayısını hesapla (hem kişisel hem sistem bildirimleri)
        const unread = allNotifications.filter(n => !n.is_read).length;
        setUnreadCount(unread);
        setLoading(false);
        return;
      }
      
      // Eğer ek kullanıcı verileri alınamazsa veya hiç bildirim yoksa
      setNotifications(allNotifications || []);
      
      // Okunmamış bildirim sayısını hesapla (hem kişisel hem sistem bildirimleri)
      const unread = allNotifications.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Bildirimi okundu olarak işaretle (hem kişisel hem sistem bildirimleri için)
  const markAsRead = async (id, opts = {}) => {
    try {
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) return;

      if (opts.isSystem) {
        // Sistem bildirimi için local storage güncelle
        const readSet = loadSystemState(session.user.id, 'read');
        readSet.add(opts.systemId);
        saveSystemState(session.user.id, 'read', readSet);
      } else {
        // Kişisel bildirim için DB güncelle
        const { error } = await supabaseClient
          .from('notifications')
          .update({ is_read: true, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (error) throw error;

        // Log the action in notification_history
        await supabaseClient
          .from('notification_history')
          .insert({
            notification_id: id,
            user_id: session.user.id,
            action: 'read'
          });
      }

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(n => 
          n.id === id ? { ...n, is_read: true } : (opts.isSystem && n.system_id === opts.systemId ? { ...n, is_read: true } : n)
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error.message);
    }
  };

  // Bildirimi okunmadı olarak işaretle (hem kişisel hem sistem bildirimleri için)
  const markAsUnread = async (id, opts = {}) => {
    try {
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) return;

      if (opts.isSystem) {
        // Sistem bildirimi için local storage güncelle
        const readSet = loadSystemState(session.user.id, 'read');
        readSet.delete(opts.systemId);
        saveSystemState(session.user.id, 'read', readSet);
      } else {
        // Kişisel bildirim için DB güncelle
        const { error } = await supabaseClient
          .from('notifications')
          .update({ is_read: false, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (error) throw error;

        // Note: Not logging 'unread' action in history as it's not in schema constraint
        // The schema only allows 'read', 'deleted', 'restored' actions
      }

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(n => 
          n.id === id ? { ...n, is_read: false } : (opts.isSystem && n.system_id === opts.systemId ? { ...n, is_read: false } : n)
        )
      );
      
      // Update unread count
      setUnreadCount(prev => prev + 1);
    } catch (error) {
      console.error('Error marking notification as unread:', error.message);
    }
  };

  // İlgili sayfaya yönlendir ve bildirimi okundu olarak işaretle
  const handleNotificationClick = async (notification) => {
    try {
      // Okunmamışsa okundu olarak işaretle
      if (!notification.is_read) {
        if (notification.is_system) {
          await markAsRead(notification.id, { isSystem: true, systemId: notification.system_id });
        } else {
          await markAsRead(notification.id);
        }
      }

      // Sistem bildirimleri için yönlendirme yapma, sadece kapat
      if (notification.is_system) {
        setShowNotifications(false);
        return;
      }

      // Bildirim tipine göre yönlendirme yap
      switch (notification.type) {
        case 'team':
          navigate('/team');
          break;
        case 'task':
          navigate('/activities');
          break;
        case 'project':
        default:
          navigate('/tasks');
          break;
      }
      
      // Bildirim menüsünü kapat
      setShowNotifications(false);
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Bildirim işlenirken bir hata oluştu');
    }
  };

  // Bildirimler değiştiğinde okunmamış sayısını güncelle
  useEffect(() => {
    const unread = notifications.filter(n => !n.is_read).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Component yüklendiğinde bildirimleri çek
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime notification subscription
  useEffect(() => {
    const setupRealtimeSubscription = async () => {
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) return;

      const channel = supabaseClient
        .channel('notifications_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${session.user.id}`
          },
          async (payload) => {
            // console.log('Notification change received:', payload);
            
            switch (payload.eventType) {
              case 'INSERT':
                // Yeni bildirimin oluşturan kullanıcı bilgilerini çek
                if (payload.new.created_by) {
                  const { data: creator, error } = await supabaseClient
                    .from('user_profiles')
                    .select('id, first_name, last_name, profile_photo_url')
                    .eq('id', payload.new.created_by)
                    .single();
                  
                  if (!error && creator) {
                    // Oluşturan bilgilerini ekle
                    payload.new.creator = creator;
                  }
                }
                
                setNotifications(prev => [payload.new, ...prev]);
                setUnreadCount(prev => prev + 1);
                
                // Bildirim toast'u göster
                if (payload.new.title) {
                  toast.success(payload.new.title, {
                    duration: 4000,
                    icon: '🔔'
                  });
                }
                break;
              case 'UPDATE':
                setNotifications(prev =>
                  prev.map(n => {
                    if (n.id === payload.new.id) {
                      return { ...payload.new, creator: n.creator };
                    }
                    return n;
                  })
                );
                break;
              case 'DELETE':
                setNotifications(prev =>
                  prev.filter(n => n.id !== payload.old.id)
                );
                break;
              default:
                break;
            }
          }
        )
        .subscribe();

      return () => {
        supabaseClient.removeChannel(channel);
      };
    };

    setupRealtimeSubscription();
  }, []);

  const handleMarkAsRead = async (e, notification) => {
    try {
      e.stopPropagation(); // Tıklama olayının yayılmasını engelle
      
      if (notification.is_system) {
        await markAsRead(notification.id, { isSystem: true, systemId: notification.system_id });
      } else {
        await markAsRead(notification.id);
      }
      
      setShowActionMenu(null); // Menüyü kapat
      toast.success('Bildirim okundu olarak işaretlendi');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Bildirim işaretlenirken bir hata oluştu');
    }
  };

  const handleMarkAsUnread = async (e, notification) => {
    try {
      e.stopPropagation(); // Tıklama olayının yayılmasını engelle
      
      if (notification.is_system) {
        await markAsUnread(notification.id, { isSystem: true, systemId: notification.system_id });
      } else {
        await markAsUnread(notification.id);
      }
      
      setShowActionMenu(null); // Menüyü kapat
      toast.success('Bildirim okunmadı olarak işaretlendi');
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      toast.error('Bildirim işaretlenirken bir hata oluştu');
    }
  };

  return (
    <>
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 border-b border-gray-200 dark:border-gray-800 backdrop-blur-xl">
        <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 shadow-md">
          {/* Mobil sidebar butonu */}
          <div className="flex items-center lg:hidden">
            <button
              onClick={onToggleSidebar}
              className="p-2 -ml-2 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          {/* Arama Alanı */}
          <div className="flex-1 flex items-center">
            <div className="w-full max-w-lg relative" ref={searchRef}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                onFocus={() => setShowResults(true)}
                placeholder="Görev veya proje ara..."
                className="w-full pl-10 pr-4 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 
                  bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 
                  placeholder-gray-500 dark:placeholder-gray-400
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  transition-colors duration-200"
              />

              {/* Arama Sonuçları */}
              <AnimatePresence>
                {showResults && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    {hasResults ? (
                      searchResultsArr.map((cat) =>
                        cat.results.length > 0 ? (
                          <div key={cat.category}>
                            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {cat.category}
                              </span>
                            </div>
                            <div className="py-2">
                              {cat.results.map((result) => (
                                <button
                                  key={result.id}
                                  onClick={() => handleResultClick(`${cat.path}/${result.id}`)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  {result.title || result.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null
                      )
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        Sonuç bulunamadı
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Sağ bölüm */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Yeni Ekle Butonu 
            <button className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
              <Plus className="w-4 h-4" />
              <span>Yeni</span>
            </button>
            */} 
            {/* Bildirimler - Stok Takip modunda gösterme */}
            {!isStockPanel && (
              <div className="relative" ref={notificationRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </motion.button>

                {/* Bildirim Penceresi */}
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ type: 'spring', duration: 0.3 }}
                      className="z-50"
                    >
                      {/* Overlay kaldırıldı */}

                      {/* Bildirim Paneli */}
                      <div
                        className="
                          fixed inset-0 
                          flex items-start justify-center pt-20
                          sm:absolute sm:inset-auto sm:right-0 sm:mt-8 sm:pt-0
                        "
                      >
                        <motion.div
                          initial={{ scale: 0.95, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.95, opacity: 0 }}
                          transition={{ type: 'spring', damping: 20, stiffness: 200 }}
                          className="w-80 sm:w-96 relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg"
                        >
                          {/* Header */}
                          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bildirimler</h3>
                            <div className="flex items-center gap-2">
                              {unreadCount > 0 && (
                                <span className="px-2.5 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                                  {unreadCount} yeni
                                </span>
                              )}
                              <motion.button
                                onClick={() => setShowNotifications(false)}
                                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                              </motion.button>
                            </div>
                          </div>

                          {/* Bildirim Listesi */}
                          <div className="max-h-[60vh] overflow-y-auto">
                            {loading ? (
                              <div className="flex justify-center items-center p-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                              </div>
                            ) : notifications.length > 0 ? (
                              <AnimatePresence mode="popLayout">
                                {notifications.map((notification) => {
                                  const NotificationType = NOTIFICATION_TYPES[notification.type]?.icon || Bell;
                                  const typeColor = NOTIFICATION_TYPES[notification.type]?.color || 'blue';
                                  
                                  return (
                                    <motion.div
                                      key={notification.id}
                                      layout
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      exit={{ opacity: 0, x: 20, scale: 0.95 }}
                                      transition={{ 
                                        duration: 0.3,
                                        layout: { duration: 0.2 }
                                      }}
                                      className={`p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-all duration-200 cursor-pointer group ${
                                        !notification.is_read ? 'bg-gray-50 dark:bg-gray-700/20' : 'opacity-75'
                                      }`}
                                      onClick={() => handleNotificationClick(notification)}
                                    >
                                      <div className="flex gap-3">
                                        <div
                                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-${typeColor}-100 dark:bg-${typeColor}-900/30 text-${typeColor}-600 dark:text-${typeColor}-400 transition-all duration-200 ${
                                            notification.is_read ? 'opacity-60' : ''
                                          }`}
                                        >
                                          <NotificationType className="w-5 h-5" />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium transition-colors duration-200 ${
                                            notification.is_read 
                                              ? 'text-gray-600 dark:text-gray-400' 
                                              : 'text-gray-900 dark:text-white'
                                          }`}>
                                            {notification.title}
                                          </p>
                                          {/* Sistem bildirimleri için dangerouslySetInnerHTML kullan */}
                                          {notification.is_system ? (
                                            <div className={`prose dark:prose-invert max-w-none mt-1 line-clamp-2 transition-colors duration-200 ${
                                              notification.is_read 
                                                ? 'text-gray-500 dark:text-gray-500' 
                                                : 'text-gray-600 dark:text-gray-400'
                                            }`} dangerouslySetInnerHTML={{ __html: notification.content }} />
                                          ) : (
                                            <p className={`text-sm line-clamp-2 transition-colors duration-200 ${
                                              notification.is_read 
                                                ? 'text-gray-500 dark:text-gray-500' 
                                                : 'text-gray-500 dark:text-gray-400'
                                            }`}>
                                              {notification.content}
                                            </p>
                                          )}
                                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{formatTime(notification.created_at)}</p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                          {!notification.is_read && (
                                            <motion.div 
                                              initial={{ scale: 0 }}
                                              animate={{ scale: 1 }}
                                              className="w-2.5 h-2.5 bg-primary-500 rounded-full"
                                            />
                                          )}
                                          
                                          {/* Action Menu Button */}
                                          <motion.button
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowActionMenu(showActionMenu === notification.id ? null : notification.id);
                                            }}
                                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors opacity-0 group-hover:opacity-100"
                                          >
                                            <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                          </motion.button>
                                        </div>
                                      </div>

                                      {/* Action Menu */}
                                      <AnimatePresence>
                                        {showActionMenu === notification.id && (
                                          <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg space-y-1"
                                          >
                                            {!notification.is_read ? (
                                              <button
                                                onClick={(e) => handleMarkAsRead(e, notification)}
                                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors flex items-center gap-2"
                                              >
                                                <Eye className="w-4 h-4" />
                                                Okundu olarak işaretle
                                              </button>
                                            ) : (
                                              <button
                                                onClick={(e) => handleMarkAsUnread(e, notification)}
                                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors flex items-center gap-2"
                                              >
                                                <Eye className="w-4 h-4" />
                                                Okunmadı olarak işaretle
                                              </button>
                                            )}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </motion.div>
                                  );
                                })}
                              </AnimatePresence>
                            ) : (
                              <div className="py-12 text-center">
                                <Bell className="mx-auto w-8 h-8 text-gray-400 dark:text-gray-600 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 animate-pulse">Bildiriminiz bulunmuyor</p>
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => {
                                  navigate('/notifications');
                                  setShowNotifications(false);
                                }}
                                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                              >
                                Tümünü Görüntüle
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Tema Değiştir */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className="p-2 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {isDark ?  <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </motion.button>

            {/* Profil Butonu */}
            {userProfile === null ? (
              // Skeleton Loading Animation
              <div className="flex items-center gap-2 p-1.5">
                <div className="animate-pulse flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full">
                    <div className="w-full h-full rounded-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-shimmer" 
                      style={{
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite linear',
                      }}
                    />
                  </div>
                  <div className="hidden sm:block space-y-2">
                    <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                    <div className="h-2 w-20 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                  </div>
                </div>
              </div>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowProfileModal(true)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <img
                  src={
                    userProfile?.profile_photo_url ||
                    getDefaultProfilePhotoUrl(userProfile?.first_name, userProfile?.last_name)
                  }
                  alt={`${userProfile?.first_name || 'User'} ${userProfile?.last_name || ''}`}
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-white dark:ring-gray-700"
                />
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200 animate-fade-in">
                    {userProfile
                      ? `${userProfile.first_name} ${userProfile.last_name}`
                      : 'Yükleniyor...'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 animate-fade-in">
                    {userProfile?.job_position || 'Pozisyon belirtilmemiş'}
                  </p>
                </div>
              </motion.button>
            )}
          </div>
        </div>
      </nav>

      {/* Profil Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          >
            {/* Arkaplan Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-black/30 dark:bg-black/80 backdrop-blur-xl"
              onClick={() => setShowProfileModal(false)}
            />

            {/* Kare Modal */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-xl w-full aspect-square bg-white/10 dark:bg-gray-800/50 backdrop-blur-xl border border-white/20 dark:border-gray-700/50 rounded-3xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Kapat Butonu */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowProfileModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/30 text-white z-10 backdrop-blur-sm"
              >
                <X className="w-5 h-5" />
              </motion.button>

              {/* Profil Fotoğrafı */}
              <div className="relative w-full h-full group">
                <motion.img
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.4 }}
                  src={
                    userProfile?.profile_photo_url ||
                    getDefaultProfilePhotoUrl(userProfile?.first_name, userProfile?.last_name)
                  }
                  alt={`${userProfile?.first_name || 'User'} ${userProfile?.last_name || ''}`}
                  className="w-full h-full object-cover"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Kullanıcı Bilgileri (hover ile yükselen kısım) */}
                <div className="absolute bottom-0 left-0 right-0 p-8 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <div className="backdrop-blur-md bg-black/30 rounded-2xl p-6">
                    <h3 className="text-2xl font-semibold text-white">
                      {userProfile
                        ? `${userProfile.first_name} ${userProfile.last_name}`
                        : 'Yükleniyor...'}
                    </h3>
                    <p className="text-sm text-gray-200 mb-4">
                      {userProfile?.job_position || 'Pozisyon belirtilmemiş'}
                    </p>
                    <div className="flex gap-4">
                      <motion.button onClick={() => navigate('/profile-info')} 
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
                      >
                        <Pencil className="w-4 h-4"  />
                        Fotoğrafı Düzenle
                      </motion.button>
                      <motion.button 
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setShowProfileModal(false);
                          navigate('/account-settings');
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                      >
                        <User className="w-4 h-4" />
                        Hesap Ayarları
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;

<style jsx>{`
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`}</style>
